import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatMoney, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Flame, ArrowRight, AlertTriangle } from "lucide-react";

function sizeKg(sizeName) {
  const m = String(sizeName || "").toLowerCase().replace("kg", "").trim();
  const n = parseFloat(m);
  return isNaN(n) ? 0 : n;
}

export default function RefillEntry() {
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    date: todayISO(),
    challan_no: "",
    bullet: "",
    from_godown: "", // source godown where MT cylinders live & filled ones return to
    vehicle_no: "",
    driver_name: "",
    notes: "",
  });
  const [items, setItems] = useState([{ brand: "", size: "", filled_qty: 0 }]);

  const load = async () => {
    const [b, s, g, st] = await Promise.all([
      api.get("/brands"), api.get("/sizes"), api.get("/godowns"), api.get("/stock/bullet"),
    ]);
    setBrands(b.data); setSizes(s.data); setGodowns(g.data);
    setStockMap(st.data || {});
    const bs = (g.data || []).filter((x) => x.kind === "bullet");
    const std = (g.data || []).filter((x) => x.kind !== "bullet");
    setForm((f) => ({
      ...f,
      bullet: f.bullet || bs[0]?.name || "",
      from_godown: f.from_godown || std[0]?.name || "",
    }));
  };
  useEffect(() => { load(); }, []);

  const bullets = useMemo(() => (godowns || []).filter((g) => g.kind === "bullet"), [godowns]);
  const standards = useMemo(() => (godowns || []).filter((g) => g.kind !== "bullet"), [godowns]);

  const totalKg = useMemo(
    () => items.reduce((s, it) => s + (Number(it.filled_qty || 0) * sizeKg(it.size)), 0),
    [items]
  );
  const totalCyl = useMemo(
    () => items.reduce((s, it) => s + Number(it.filled_qty || 0), 0),
    [items]
  );
  const bulletStock = stockMap[form.bullet] || 0;
  const willGoNegative = totalKg > bulletStock + 0.001;

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addRow = () => setItems((arr) => [...arr, { brand: "", size: "", filled_qty: 0 }]);
  const removeRow = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bullet) { toast.error("Bullet godown required"); return; }
    if (!form.from_godown) { toast.error("Source godown (where MT lives) required"); return; }
    const filtered = items.filter((it) => it.brand && it.size && Number(it.filled_qty || 0) > 0);
    if (filtered.length === 0) { toast.error("Add at least one cylinder line"); return; }
    if (willGoNegative && !window.confirm(`This refill needs ${formatMoney(Math.round(totalKg * 100) / 100)} KG but the bullet has only ${formatMoney(Math.round(bulletStock * 100) / 100)} KG. Continue anyway?`)) {
      return;
    }
    setBusy(true);
    try {
      await api.post("/transactions", {
        type: "refill",
        date: form.date,
        challan_no: form.challan_no || "",
        bullet_godown: form.bullet,
        from_godown: form.from_godown,
        to_godown: form.from_godown,
        vehicle_no: form.vehicle_no || "",
        driver_name: form.driver_name || "",
        notes: form.notes || "",
        items: filtered.map((it) => ({
          brand: it.brand, size: it.size,
          filled_qty: Number(it.filled_qty),
          mt_qty: Number(it.filled_qty), // each filled cyl came from 1 MT
          rate: 0, amount: 0,
        })),
        total_amount: 0,
      });
      toast.success(`Refill saved · ${totalCyl} cyl · ${formatMoney(Math.round(totalKg * 100) / 100)} KG drawn from ${form.bullet}`);
      setForm({
        date: todayISO(), challan_no: "", bullet: form.bullet, from_godown: form.from_godown,
        vehicle_no: "", driver_name: "", notes: "",
      });
      setItems([{ brand: "", size: "", filled_qty: 0 }]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="entry-form-refill">
      <p className="small-label text-gray-500">
        Refill · take <span className="font-bold">MT (empty)</span> cylinders from a source godown, fill them from a bullet tank, return them as <span className="font-bold">Filled</span> to the same godown. KG is drawn from the bullet.
      </p>

      {bullets.length === 0 && (
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A] flex items-start gap-3">
          <AlertTriangle className="text-[#B45309] flex-shrink-0" size={20} />
          <div className="text-sm">
            <div className="font-bold text-[#B45309]">No Bullet godowns yet</div>
            <div className="text-[#92400E]">Create one in Masters → Godowns and set kind = <span className="font-bold">Bullet</span>.</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="small-label block mb-2">Date</label>
          <input type="date" className="input-lg" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="refill-date" required />
        </div>
        <div>
          <label className="small-label block mb-2">Challan No</label>
          <input className="input-lg" value={form.challan_no}
            onChange={(e) => setForm({ ...form, challan_no: e.target.value })}
            data-testid="refill-challan" />
        </div>
        <div>
          <label className="small-label block mb-2">Bullet (KG source)</label>
          <select className="input-lg" value={form.bullet}
            onChange={(e) => setForm({ ...form, bullet: e.target.value })}
            required data-testid="refill-bullet">
            <option value="">— Select bullet —</option>
            {bullets.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="small-label block mb-2">Cylinder Godown</label>
          <select className="input-lg" value={form.from_godown}
            onChange={(e) => setForm({ ...form, from_godown: e.target.value })}
            required data-testid="refill-godown">
            <option value="">— Select godown —</option>
            {standards.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </div>
      </div>

      {/* Stock summary */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap text-sm">
        <div className="flex items-center gap-1.5 text-[#B45309] font-semibold">
          <Flame size={14} /> {form.bullet || "—"} stock:
          <span className="data-num font-bold">{formatMoney(Math.round(bulletStock * 100) / 100)} KG</span>
        </div>
        <ArrowRight size={14} className="text-gray-400" />
        <div className="flex items-center gap-1.5">
          To draw: <span className="data-num font-bold">{formatMoney(Math.round(totalKg * 100) / 100)} KG</span>
          <span className="text-gray-500">({totalCyl} cyl)</span>
        </div>
        {willGoNegative && (
          <div className="ml-auto text-xs text-[#B91C1C] font-semibold flex items-center gap-1">
            <AlertTriangle size={12} /> Bullet will go negative
          </div>
        )}
      </div>

      {/* Item lines */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Brand</th>
              <th className="text-left">Size</th>
              <th className="num">Cyl to refill</th>
              <th className="num">KG drawn</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const drawn = Math.round(Number(it.filled_qty || 0) * sizeKg(it.size) * 100) / 100;
              return (
                <tr key={idx} data-testid={`refill-row-${idx}`}>
                  <td>
                    <select className="input-lg h-9" value={it.brand}
                      onChange={(e) => updateItem(idx, { brand: e.target.value })}
                      data-testid={`refill-brand-${idx}`}>
                      <option value="">—</option>
                      {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="input-lg h-9" value={it.size}
                      onChange={(e) => updateItem(idx, { size: e.target.value })}
                      data-testid={`refill-size-${idx}`}>
                      <option value="">—</option>
                      {sizes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="number" min="0" className="input-lg h-9 text-right data-num"
                      value={it.filled_qty}
                      onChange={(e) => updateItem(idx, { filled_qty: e.target.value })}
                      data-testid={`refill-qty-${idx}`} />
                  </td>
                  <td className="num data-num">{formatMoney(drawn)}</td>
                  <td className="text-right">
                    <button type="button" onClick={() => removeRow(idx)}
                      className="text-gray-400 hover:text-[#B91C1C]" data-testid={`refill-del-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 border-t border-gray-200">
          <button type="button" onClick={addRow} className="text-xs font-semibold text-gray-600 hover:text-black flex items-center gap-1" data-testid="refill-add-row">
            <Plus size={14} /> Add line
          </button>
        </div>
      </div>

      {/* Vehicle / notes */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="small-label block mb-2">Vehicle</label>
          <input className="input-lg" value={form.vehicle_no}
            onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })}
            data-testid="refill-vehicle" />
        </div>
        <div>
          <label className="small-label block mb-2">Driver</label>
          <input className="input-lg" value={form.driver_name}
            onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
            data-testid="refill-driver" />
        </div>
        <div>
          <label className="small-label block mb-2">Notes</label>
          <input className="input-lg" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="refill-notes" />
        </div>
      </div>

      <div className="flex justify-end">
        <button disabled={busy || bullets.length === 0} className="btn-primary h-12 px-8 flex items-center gap-2" data-testid="refill-save">
          <Save size={16} /> {busy ? "Saving…" : "Save Refill"}
        </button>
      </div>
    </form>
  );
}
